var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useMemo, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Status } from "../models";
import { fetchNotifications } from "./FetchNotifications";
import { markNotificationDismissed, markNotificationRead, markSubNotificationRead, markNotificationsDisplayed } from "./MarkNotification";
import { notificationMetricsMap } from "../utils/metrics";
import { sleep } from "../utils/sleep";
import crypto from "crypto-js";
// Stringifies a notification to only the parts relevant for comparison (so eventId and properties subject to change)
function stringify(notification) {
    var relevant = notification.eventId + ":" + notification.status;
    if (notification.aggregated) {
        relevant += ":[" + notification.subNotifications.join(",") + "]";
    }
    return relevant;
}
function stringifyList(notifications) {
    return notifications.map(function (n) { return stringify(n); }).join(";");
}
function immutableUpdate(all, toUpdate, status) {
    return all.map(function (n) {
        if (toUpdate.indexOf(n) < 0) {
            return n;
        }
        else {
            return __assign(__assign({}, n), { status: status });
        }
    });
}
export function useNotifications(placement, searchParams, accountInfoParams) {
    var _a = useState([]), notifications = _a[0], setNotifications = _a[1];
    var _b = useState(0), total = _b[0], setTotal = _b[1];
    var _c = useState(), forceRefresh = _c[0], setForceRefresh = _c[1];
    var _d = useState(true), loading = _d[0], setLoading = _d[1];
    useEffect(function () {
        var cancelled = false;
        function fetch() {
            return __awaiter(this, void 0, void 0, function () {
                var response_1, error_1;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            _a.trys.push([0, 2, , 3]);
                            return [4 /*yield*/, fetchNotifications(placement, searchParams, accountInfoParams)];
                        case 1:
                            response_1 = _a.sent();
                            // TODO: Remove once we've updated CommunicationsHubFeedService to Md5 hash aggregate eventIds for all notifications
                            response_1.notifications.forEach(function (n) {
                                if (n.aggregated && n.subNotifications) {
                                    n.eventId = crypto.MD5(n.subNotifications.toString()).toString(crypto.enc.Hex);
                                }
                            });
                            if (cancelled) {
                                return [2 /*return*/];
                            }
                            // Unstable is scary but apparently it's the recommended solution by the React team:
                            // https://github.com/facebook/react/issues/14259#issuecomment-505918440
                            unstable_batchedUpdates(function () {
                                setLoading(false);
                                setTotal(response_1.total);
                                setNotifications(function (previous) {
                                    if (stringifyList(previous) === stringifyList(response_1.notifications)) {
                                        return previous;
                                    }
                                    else {
                                        return response_1.notifications;
                                    }
                                });
                            });
                            notificationMetricsMap.get(placement).publishCounterMonitor('fetchNotification', 1);
                            return [3 /*break*/, 3];
                        case 2:
                            error_1 = _a.sent();
                            console.error("Error fetching notifications", error_1);
                            notificationMetricsMap.get(placement).publishCounterMonitor('fetchNotificationFailure', 1);
                            return [3 /*break*/, 3];
                        case 3: return [2 /*return*/];
                    }
                });
            });
        }
        setLoading(true);
        fetch();
        // Cancel previous fetch if a new one happens before the previous is done
        return function () { cancelled = true; };
    }, [placement, searchParams, accountInfoParams, forceRefresh]);
    var refresh = useCallback(function () { return setForceRefresh(Symbol()); }, []);
    // If no notifications are given, then we mark all the fetched ones as displayed.
    var markDisplayed = useCallback(function (toMark) {
        if (toMark === void 0) { toMark = notifications; }
        return __awaiter(this, void 0, void 0, function () {
            var error_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, , 5]);
                        return [4 /*yield*/, markNotificationsDisplayed(toMark, accountInfoParams, placement)];
                    case 2:
                        _a.sent();
                        // Status updates can take up to 1s to be reflected, wasteful to refresh before update is reflected.
                        return [4 /*yield*/, sleep(1000)];
                    case 3:
                        // Status updates can take up to 1s to be reflected, wasteful to refresh before update is reflected.
                        _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        error_2 = _a.sent();
                        console.error("Error marking notifications as displayed", error_2);
                        return [3 /*break*/, 5];
                    case 5:
                        refresh();
                        return [2 /*return*/];
                }
            });
        });
    }, [notifications, accountInfoParams, placement, refresh]);
    var markRead = useCallback(function (toMark) {
        return __awaiter(this, void 0, void 0, function () {
            var error_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        // Optimistic guess for snappy UI
                        setNotifications(immutableUpdate(notifications, [toMark], Status.READ));
                        return [4 /*yield*/, markNotificationRead(toMark, accountInfoParams, placement)];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        error_3 = _a.sent();
                        console.error("Error marking notification read", error_3);
                        return [3 /*break*/, 4];
                    case 4:
                        refresh();
                        return [2 /*return*/];
                }
            });
        });
    }, [notifications, accountInfoParams, placement, refresh]);
    var markReadSubNotification = useCallback(function (toMark) {
        return __awaiter(this, void 0, void 0, function () {
            var error_4;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        //updating by string should only be done for individual subnotifications.
                        //Attempting to update an entire aggregated notification by passing its eventID to this function will lead to incorrect behavior.
                        setLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, markSubNotificationRead(toMark, accountInfoParams, placement)];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        error_4 = _a.sent();
                        console.error("Error marking notification read", error_4);
                        return [3 /*break*/, 4];
                    case 4:
                        refresh();
                        return [2 /*return*/];
                }
            });
        });
    }, [accountInfoParams, placement, refresh]);
    var markDismissed = useCallback(function (toMark) {
        return __awaiter(this, void 0, void 0, function () {
            var error_5;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        // TODO: Optimistic guess, once we put dismiss feature back.
                        return [4 /*yield*/, markNotificationDismissed(toMark, accountInfoParams, placement)];
                    case 2:
                        // TODO: Optimistic guess, once we put dismiss feature back.
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        error_5 = _a.sent();
                        console.error("Error marking notification dismissed", error_5);
                        return [3 /*break*/, 4];
                    case 4:
                        refresh();
                        return [2 /*return*/];
                }
            });
        });
    }, [accountInfoParams, placement, refresh]);
    var actions = useMemo(function () { return ({
        refresh: refresh,
        display: markDisplayed,
        read: markRead,
        readSubNotification: markReadSubNotification,
        dismiss: markDismissed
    }); }, [refresh, markDisplayed, markRead, markReadSubNotification, markDismissed]);
    // This is getting a bit too much. Let's ship this for now but I think we want to split the hook, not sure how yet.
    // Or maybe return an actual object, because this array is becoming messy.
    return [notifications, total, loading, actions];
}
